import React, { useCallback, useEffect, useState, type FC } from "react";

import notification from "@app/services/notification";
import "./SettingsClient.scss";

import { reportError } from "@app/services/error-reporter";
import classNames from "classnames";
import { useAuthStore } from "@app/store/useAuthStore";
import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import { loadAppConfig, type ServerHost } from "@app/config/app/appConfig";
import { DetailList } from "@shared/ui/detail-row";
import { Button } from "@shared/ui/button";
import { CardHeader } from "@shared/ui/card-header";
import { PickerOptionCard } from "@shared/ui/picker-option";
import { Select } from "@shared/ui/select";
import { useNavigate } from "react-router-dom";
import { AppPaths } from "@app/router/paths";

type ServerOption = ServerHost & { available: boolean };

function ServerAvailabilityIndicator({ available }: { available: boolean }) {
    return (
        <div
            className={classNames("setting_server_option__indicator", {
                "setting_server_option__indicator--available": available,
            })}
        />
    );
}

/** Компактная строка для popover. */
function renderServerPopoverOption(option: ServerOption) {
    return (
        <div className="setting_server_option">
            <ServerAvailabilityIndicator available={option.available} />
            <span>
                <b>{option.name}</b> - {option.url}
            </span>
        </div>
    );
}

/** Fullscreen — стандартный {@link PickerOptionCard} chrome + индикатор в title. */
function renderServerFullscreenOption(option: ServerOption) {
    return (
        <PickerOptionCard
            title={
                <span className="setting_server_option">
                    <ServerAvailabilityIndicator available={option.available} />
                    <span>{option.name}</span>
                </span>
            }
            details={[{ label: "Адрес", value: option.url }]}
        />
    );
}

export const SettingsClient: FC = () => {
    const [servers, setServers] = useState<ServerOption[] | null>(null);
    const { server, clearSessionState } = useAuthStore();
    const [selectedServer, setSelectedServer] = useState<ServerHost | null>(server);

    const navigate = useNavigate();

    const pingServer = async (url: string): Promise<void> => {
        await Promise.race([
            new Promise<void>((resolve, reject) => {
                fetch(url)
                    .then(() => {
                        resolve();
                    })
                    .catch(reject);
            }),
            new Promise<void>((_resolve, reject) => {
                setTimeout(() => {
                    reject(new Error("Timeout"));
                }, 5000);
            }),
        ]);
    };

    const pingAllServers = useCallback((serverList: ServerOption[]): void => {
        serverList.forEach((serverItem) => {
            pingServer(serverItem.url)
                .then(() => {
                    setServers((prev) => {
                        if (prev == null) return prev;
                        return prev.map((item) => (item.url === serverItem.url ? { ...item, available: true } : item));
                    });
                })
                .catch(() => {});
        });
    }, []);

    const loadServers = useCallback((): void => {
        loadAppConfig()
            .then(({ servers: jsonServers }) => {
                const serverItems = jsonServers.map((item) => ({ ...item, available: false }));
                setServers(serverItems);
                pingAllServers(serverItems);
            })
            .catch((error) => reportError(error, { from: "SettingsClient.loadServers" }));
    }, [pingAllServers]);

    const handleSaveServer = (): void => {
        if (selectedServer == null) {
            notification.warning("Не выбран сервер");
            return;
        }
        ompApiClient.setServerAsHost(selectedServer);
        notification.success(`Выбранный сервер "${selectedServer.name}" сохранён. Пройдите авторизацию.`);
        clearSessionState();
        navigate(AppPaths.AUTH);
    };

    useEffect(() => {
        loadServers();
    }, [loadServers]);

    return (
        <div className="setting_choose_server_page">
            <CardHeader className="setting_choose_server_page_header" text="Выбор сервера" />
            <div className="setting_choose_server_content">
                <DetailList>
                    <DetailList.Row label="Текущий адрес" value={server?.url ?? "Не задано"} />
                </DetailList>
                <div className="field_server">
                    <Select
                        className="select_server"
                        placeholder="Выбор сервера"
                        options={servers}
                        isLoading={servers == null}
                        getOptionData={(option) => ({
                            value: option.url,
                            label: `${option.name} - ${option.url}`,
                        })}
                        optionSlots={{
                            popover: renderServerPopoverOption,
                            fullscreen: renderServerFullscreenOption,
                        }}
                        value={selectedServer?.url ?? null}
                        onChange={(url) => {
                            if (url == null || servers == null) {
                                setSelectedServer(null);
                                return;
                            }
                            setSelectedServer(servers.find((item) => item.url === url) ?? null);
                        }}
                    />
                    <Button className="btn_set" variant="primary" onClick={handleSaveServer}>
                        Установить
                    </Button>
                </div>
                <div>После смены адреса сервера, необходимо перезайти в учётную запись, чтобы изменения применились</div>
            </div>
        </div>
    );
};
